/**
 * The community's name behind a page title — unless the title already says it.
 *
 * `API Connect Conference 2026 · APICON - API Connect Conference Hub` says the
 * same thing twice and runs to 65 characters, which is about where WhatsApp
 * truncates. The suffix exists to give an origin to a title that has none of
 * its own: a space called "Comunicados" needs it. One that already carries the
 * community's name does not.
 *
 * Plain JavaScript, and shared on purpose. `server.js` writes the title a link
 * bot sees and the app writes the one the tab shows; when the two disagree the
 * title changes under the reader a second after the page opens. A copy in each
 * would drift.
 */

/** Words that carry no identity, so sharing one proves nothing. */
const STOPWORDS = new Set([
  'de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'por', 'com',
  'the', 'of', 'and', 'for',
  'hub', 'comunidade', 'community', 'portal', 'oficial'
]);

/** The meaningful words, unaccented and without the too-short ones. */
const tokens = (text) =>
  new Set(
    String(text ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 2 && !STOPWORDS.has(word))
  );

/** Two texts say the same thing when the shorter is almost entirely inside the other. */
export const saysTheSame = (title, communityName) => {
  const a = tokens(title);
  const b = tokens(communityName);
  if (!a.size || !b.size) return false;
  const shared = [...a].filter((word) => b.has(word)).length;
  return shared / Math.min(a.size, b.size) >= 0.6;
};

/**
 * The title as it should read, with the community behind it when that adds
 * something. An empty community name leaves the title alone.
 */
export const withinCommunity = (title, communityName) =>
  !communityName || saysTheSame(title, communityName) ? title : `${title} · ${communityName}`;
